// Pratiche semplici di CAF (ISEE, 730/740), Patronato (NASpI, Assegno Unico, ...) e Servizi (Cambio residenza):
// stessa pagina, `tipo` come in backend/routes/caf.py — versione semplice:
// si inserisce il cliente dall'anagrafica; quando la pratica è fatta si carica il documento finale.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import ClienteConDocumento from '../components/clients/ClienteConDocumento';
import { NuovoAppuntamento, DettaglioAppuntamento, quando } from '../components/caf/Appuntamenti';
import { Plus, Search, Trash2, Upload, FileText, Check, Clock, Loader2, ArrowUp, ArrowDown, ArrowUpDown, Printer } from 'lucide-react';
import { DelegaGeneraleBadge } from '../components/DelegaGenerale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const dataOra = (iso) => (iso ? new Date(iso).toLocaleDateString('it-IT') : '');

// Scheda del cliente scelto: tutti i dati dell'anagrafica, correggibili prima di inserire la pratica.
const CAMPI_CLIENTE = [
  ['last_name', 'Cognome'], ['first_name', 'Nome'], ['gender', 'Sesso (M/F)'], ['birth_date', 'Data di nascita', 'date'],
  ['birth_comune', 'Comune di nascita'], ['birth_provincia', 'Prov. nascita'], ['birth_country', 'Nazione (se estero)'],
  ['fiscal_code', 'Codice fiscale'], ['address', 'Indirizzo'], ['address_number', 'Civico'], ['city', 'Città di residenza'],
  ['city_provincia', 'Provincia'], ['postal_code', 'CAP'], ['phone', 'Cellulare'], ['email', 'Email'],
];
const MAIUSCOLO = new Set(['last_name', 'first_name', 'gender', 'birth_comune', 'birth_provincia', 'birth_country', 'fiscal_code', 'address', 'address_number', 'city', 'city_provincia']);
const SIGLE_DOC = { CIE: "Carta d'identità", PSE: 'Permesso di soggiorno', PASS: 'Passaporto', PAT: 'Patente' };

function SchedaCliente({ clientId, onModifiche }) {
  const [originale, setOriginale] = useState(null);
  const [c, setC] = useState(null);
  useEffect(() => {
    setC(null); setOriginale(null);
    if (!clientId) return;
    axios.get(`${API}/clients/${clientId}`).then((r) => { setOriginale(r.data); setC(r.data); }).catch(() => toast.error('Cliente non caricato'));
  }, [clientId]);
  useEffect(() => {
    const cambiati = c && originale && CAMPI_CLIENTE.some(([k]) => (c[k] || '') !== (originale[k] || ''));
    onModifiche(cambiati ? c : null);
  }, [c, originale, onModifiche]);
  if (!clientId) return null;
  if (!c) return <div className="py-4 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  const documenti = (c.documents || []).filter((d) => /^(CIE|PSE|PASS|PAT) /.test(d.description || ''));
  return (
    <div className="rounded-lg border p-4 space-y-3 bg-slate-50/60">
      <div className="text-sm font-semibold text-slate-700">Dati del cliente (dall'anagrafica — puoi correggerli)</div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {CAMPI_CLIENTE.map(([k, etichetta, tipoCampo]) => (
          <div key={k} className={k === 'address' || k === 'email' ? 'md:col-span-2' : ''}>
            <Label className="text-xs text-slate-500">{etichetta}</Label>
            <Input type={tipoCampo || 'text'} value={c[k] || ''} className={k === 'fiscal_code' ? 'font-mono' : ''}
              onChange={(e) => setC({ ...c, [k]: MAIUSCOLO.has(k) ? e.target.value.toUpperCase() : e.target.value })} />
          </div>
        ))}
      </div>
      <div>
        <Label className="text-xs text-slate-500">Documento d'identità in anagrafica</Label>
        {documenti.length === 0
          ? <p className="text-sm text-amber-700 mt-1">Nessun documento d'identità: caricalo qui sopra, viene allegato al cliente.</p>
          : documenti.map((d) => (
            <div key={d.id} className="text-sm mt-1 flex items-center gap-2">
              <FileText className="w-4 h-4 text-red-600" />
              <span>{SIGLE_DOC[d.description.split(' ')[0]] || d.description}</span>
              {d.expiry_date && <span className={`text-xs ${d.expiry_date < new Date().toISOString().slice(0, 10) ? 'text-red-700 font-semibold' : 'text-slate-500'}`}>
                scadenza {d.expiry_date.split('-').reverse().join('/')}{d.expiry_date < new Date().toISOString().slice(0, 10) ? ' (SCADUTO)' : ''}</span>}
            </div>
          ))}
      </div>
    </div>
  );
}

// Inserimento del cliente uguale alle Dimissioni: si sceglie dall'anagrafica oppure si carica il
// documento d'identità (letto in automatico); se non è in anagrafica il cliente viene creato.
function NuovaPratica({ tipo, nome, onChiudi, onCreata }) {
  const ref = useRef(null);
  const [clients, setClients] = useState([]);
  const [attesa, setAttesa] = useState(false);
  const [scelto, setScelto] = useState('');
  const [modifiche, setModifiche] = useState(null);   // dati cliente corretti (da salvare in anagrafica)
  const quandoCliente = useCallback((id) => setScelto(id || ''), []);
  const quandoModifiche = useCallback((c) => setModifiche(c), []);
  useEffect(() => {
    axios.get(`${API}/clients`).then((r) => setClients(Array.isArray(r.data) ? r.data : (r.data.items || [])))
      .catch(() => toast.error('Errore nel caricamento dei clienti'));
  }, []);
  const crea = async () => {
    setAttesa(true);
    const clientId = await ref.current?.conferma();
    if (!clientId) { setAttesa(false); return; }
    try {
      if (modifiche && modifiche.id === clientId) await axios.put(`${API}/clients/${clientId}`, modifiche);   // correzioni all'anagrafica
    } catch (err) { toast.error(err.response?.data?.detail || 'Dati del cliente non salvati'); setAttesa(false); return; }
    try { await axios.post(`${API}/caf/${tipo}`, { client_id: clientId }); toast.success(`${nome} inserito`); onCreata(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Non inserito'); setAttesa(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onChiudi(); }}>
      {/* alta quasi quanto lo schermo: l'elenco dei clienti mentre si cerca resta tutto visibile */}
      <DialogContent className="sm:max-w-3xl h-[90vh] max-h-[90vh] overflow-y-auto flex flex-col">
        <DialogHeader>
          <DialogTitle>Nuovo {nome}</DialogTitle>
          <DialogDescription>Scegli il cliente o carica il suo documento: se non è in anagrafica lo registro io.</DialogDescription>
        </DialogHeader>
        <ClienteConDocumento ref={ref} clients={clients} setClients={setClients} tipoDocumentoIniziale="Carta d'identità elettronica" onCliente={quandoCliente} />
        <SchedaCliente clientId={scelto} onModifiche={quandoModifiche} />
        <div className="flex-1" />
        <DialogFooter>
          <Button variant="outline" onClick={onChiudi}>Annulla</Button>
          <Button onClick={crea} disabled={attesa}>{attesa ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} {modifiche ? 'Salva dati e inserisci' : 'Inserisci'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Patto di Attivazione Digitale (PAD) dopo la NASpI: Mandato EPAS (Ministero del Lavoro) + Consenso,
// e PDF unico da stampare con il documento d'identità — come le Dimissioni
const CON_DELEGA_GENERALE = ['cambio_residenza'];   // pratiche dove serve la delega generale del cliente
const CON_PAD = ['naspi'];                       // ricevuta PAD separata (oltre alla ricevuta NASpI)
const CON_MANDATO = { naspi: 'PAD', test_a2: 'Mandato' };   // pulsante del mandato EPAS sulla riga

function FinestraPad({ tipo, p, onChiudi, onFatto }) {
  const [d, setD] = useState(null);
  const [attesa, setAttesa] = useState('');
  const [correggi, setCorreggi] = useState(false);
  const [ricevuta, setRicevuta] = useState(p.pad?.ricevuta || null);
  const caricaRicevuta = async (file) => {
    if (!file) return;
    setAttesa('ricevuta');
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await axios.post(`${API}/caf/${tipo}/${p.id}/pad/ricevuta`, fd);
      setRicevuta(r.data.pad?.ricevuta || null); toast.success('Ricevuta PAD caricata'); onFatto();
    } catch (err) { toast.error(err.response?.data?.detail || 'Caricamento non riuscito'); } finally { setAttesa(''); }
  };
  const apriRicevuta = async () => {
    const finestra = window.open('', '_blank');
    try {
      const r = await axios.get(`${API}/caf/${tipo}/${p.id}/pad/ricevuta`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      if (finestra) finestra.location.href = url; else window.location.href = url;
    } catch (_) { if (finestra) finestra.close(); toast.error('Ricevuta non disponibile'); }
  };
  const togliRicevuta = async () => {
    if (!window.confirm('Togliere la ricevuta PAD caricata?')) return;
    try { await axios.delete(`${API}/caf/${tipo}/${p.id}/pad/ricevuta`); setRicevuta(null); onFatto(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Non tolta'); }
  };
  useEffect(() => {
    axios.get(`${API}/caf/${tipo}/${p.id}/pad`).then((r) => setD(r.data)).catch(() => { toast.error('Mandato non caricato, riprova'); onChiudi(); });
  }, [tipo, p.id, onChiudi]);
  const apriPdf = async (quale) => {
    setAttesa(quale);
    const finestra = window.open('', '_blank');
    try {
      await axios.put(`${API}/caf/${tipo}/${p.id}/pad`, { cittadinanza: d.cittadinanza, operatore: d.operatore });
      const r = await axios.get(`${API}/caf/${tipo}/${p.id}/pad/${quale}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      if (finestra) finestra.location.href = url; else window.location.href = url;
      onFatto();
    } catch (err) { if (finestra) finestra.close(); toast.error(err.response?.data?.detail || 'PDF non creato'); } finally { setAttesa(''); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onChiudi(); }}>
      <DialogContent className="w-[95vw] sm:max-w-lg overflow-hidden">
        <DialogHeader>
          <DialogTitle>{d?.titolo || 'Mandato EPAS'}</DialogTitle>
          <DialogDescription>{p.client_name} · Mandato EPAS e consenso da far firmare al cliente.</DialogDescription>
        </DialogHeader>
        {!d ? <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : (
          <div className="space-y-3 min-w-0">
            <div className="text-sm">
              <span className="text-slate-500">Cittadinanza (dall'anagrafica): </span>
              {correggi
                ? <Input autoFocus className="mt-1" value={d.cittadinanza} onChange={(e) => setD({ ...d, cittadinanza: e.target.value.toUpperCase() })} />
                : <><b>{d.cittadinanza || '—'}</b> <button type="button" className="ml-2 text-xs text-blue-700 underline" onClick={() => setCorreggi(true)}>correggi</button></>}
            </div>
            <div>
              <Label>Operatore del Patronato (nome e firma sul mandato)</Label>
              <select className="block w-full max-w-full mt-1 h-10 rounded-md border px-3 text-sm bg-white" value={d.operatore} onChange={(e) => setD({ ...d, operatore: e.target.value })}>
                {d.operatori.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            {d.stampato_il && <p className="text-xs text-slate-500">Mandato stampato il {dataOra(d.stampato_il)}</p>}
            {d.ricevuta_separata && <div className="rounded-lg border p-3 space-y-2">
              <div className="text-sm font-medium">Ricevuta del PAD</div>
              {ricevuta ? (
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <button type="button" onClick={apriRicevuta} className="inline-flex items-center gap-1 text-blue-700 hover:underline min-w-0 max-w-full">
                    <FileText className="w-4 h-4 text-red-600 shrink-0" /><span className="truncate">{ricevuta.nome}</span>
                  </button>
                  <span className="text-xs text-green-700">caricata il {dataOra(ricevuta.caricato_il)}</span>
                  <button type="button" onClick={togliRicevuta} className="text-xs text-red-600 underline">togli</button>
                </div>
              ) : <p className="text-xs text-amber-700">Non ancora caricata: caricala quando il PAD è sottoscritto.</p>}
              <label className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-md border text-xs cursor-pointer ${ricevuta ? 'bg-white hover:bg-slate-50' : 'bg-slate-900 text-white hover:bg-slate-800'}`}>
                {attesa === 'ricevuta' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {ricevuta ? 'Sostituisci ricevuta' : 'Carica ricevuta PAD'}
                <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" className="hidden"
                  onChange={(e) => { caricaRicevuta(e.target.files?.[0]); e.target.value = ''; }} />
              </label>
            </div>}
          </div>
        )}
        <DialogFooter className="flex-wrap gap-2 sm:space-x-0">
          <Button variant="outline" disabled={!d || !!attesa} onClick={() => apriPdf('mandato-pdf')}>
            {attesa === 'mandato-pdf' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />} Mandato + consenso
          </Button>
          <Button disabled={!d || !!attesa} onClick={() => apriPdf('stampa-pdf')}>
            {attesa === 'stampa-pdf' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Printer className="w-4 h-4 mr-2" />} Stampa tutto (con documento)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// nome della pratica, sezione del menu, testo del pulsante di caricamento
const TIPI = {
  isee: ['ISEE', 'CAF', 'ISEE'],
  '730': ['730/740 Redditi', 'CAF', '730/740'],
  naspi: ['NASpI', 'Patronato', 'ricevuta NASpI'],
  assegno_unico: ['Assegno Unico', 'Patronato', 'ricevuta domanda'],
  maternita_inps: ['Maternità INPS', 'Patronato', 'ricevuta domanda'],
  bonus_nido: ['Bonus nido INPS', 'Patronato', 'ricevuta domanda'],
  test_a2: ['Test italiano A2', 'Patronato', 'ricevuta / esito'],
  ricongiungimento: ['Ricongiungimento familiare', 'Patronato', 'ricevuta domanda'],
  cambio_residenza: ['Cambio residenza', 'Servizi', 'ricevuta Comune'],
  assegno_sociale: ['Assegno sociale', 'Patronato · INPS', 'ricevuta domanda'],
  assegno_invalidita: ['Assegno ordinario di invalidità', 'Patronato · INPS', 'ricevuta domanda'],
  assegno_invalidita_civile: ['Assegno di invalidità civile', 'Patronato · INPS', 'ricevuta domanda'],
  pensione_invalidita_civile: ['Pensione di invalidità civile', 'Patronato · INPS', 'ricevuta domanda'],
  indennita_frequenza: ['Indennità di frequenza', 'Patronato · INPS', 'ricevuta domanda'],
  bonus_nuovi_nati: ['Bonus nuovi nati', 'Patronato · INPS', 'ricevuta domanda'],
  bonus_mamme: ['Bonus mamme', 'Patronato · INPS', 'ricevuta domanda'],
  assegno_inclusione: ['Assegno di inclusione (ADI)', 'Patronato · INPS', 'ricevuta domanda'],
};
const CON_APPUNTAMENTO = ['isee', '730'];

export default function CafPratiche({ tipo }) {
  const [dati, setDati] = useState({ items: [], total: 0, conteggi: {}, nome: '' });
  const [cerca, setCerca] = useState(() => new URLSearchParams(window.location.search).get('cerca') || ''); // ?cerca= dal riepilogo cliente
  const [stato, setStato] = useState('');
  const [pagina, setPagina] = useState(1);
  const [ordine, setOrdine] = useState({ campo: 'inserito', verso: 'desc' });
  const [nuova, setNuova] = useState(false);
  const [carico, setCarico] = useState('');
  const [fissa, setFissa] = useState(null);       // pratica per cui fissare l'appuntamento
  const [appAperto, setAppAperto] = useState(null);
  const [pad, setPad] = useState(null);           // pratica NASpI di cui fare il PAD
  const chiudiPad = useCallback(() => setPad(null), []);
  const [nome, sezione, docNome] = TIPI[tipo] || [tipo, '', 'documento'];
  const conAppuntamento = CON_APPUNTAMENTO.includes(tipo);

  useEffect(() => { setStato(''); setCerca(''); setPagina(1); }, [tipo]);
  const carica = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/caf/${tipo}`, { params: { search: cerca || undefined, stato: stato || undefined, page: pagina, page_size: 30, ordina: ordine.campo, verso: ordine.verso } });
      setDati(r.data);
    } catch (_) { toast.error('Errore nel caricamento'); }
  }, [tipo, cerca, stato, pagina, ordine]);
  useEffect(() => { const t = setTimeout(carica, 250); return () => clearTimeout(t); }, [carica]);

  const caricaDocumento = async (p, file) => {
    if (!file) return;
    setCarico(p.id);
    try {
      const fd = new FormData(); fd.append('file', file);
      await axios.post(`${API}/caf/${tipo}/${p.id}/documento`, fd);
      toast.success(`${nome} caricato: pratica fatta`); carica();
    } catch (err) { toast.error(err.response?.data?.detail || 'Caricamento non riuscito'); } finally { setCarico(''); }
  };
  const apri = async (p) => {
    const finestra = window.open('', '_blank');
    try {
      const r = await axios.get(`${API}/caf/${tipo}/${p.id}/documento`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      if (finestra) finestra.location.href = url; else window.location.href = url;
    } catch (_) { if (finestra) finestra.close(); toast.error('Documento non disponibile'); }
  };
  // ricevuta del PAD direttamente dalla riga (come la ricevuta NASpI)
  const caricaRicevutaPad = async (p, file) => {
    if (!file) return;
    setCarico(`pad-${p.id}`);
    try {
      const fd = new FormData(); fd.append('file', file);
      await axios.post(`${API}/caf/${tipo}/${p.id}/pad/ricevuta`, fd);
      toast.success('Ricevuta PAD caricata'); carica();
    } catch (err) { toast.error(err.response?.data?.detail || 'Caricamento non riuscito'); } finally { setCarico(''); }
  };
  const apriRicevutaPad = async (p) => {
    const finestra = window.open('', '_blank');
    try {
      const r = await axios.get(`${API}/caf/${tipo}/${p.id}/pad/ricevuta`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      if (finestra) finestra.location.href = url; else window.location.href = url;
    } catch (_) { if (finestra) finestra.close(); toast.error('Ricevuta non disponibile'); }
  };
  const elimina = async (p) => {
    if (!window.confirm(`Eliminare ${nome} di ${p.client_name}?`)) return;
    try { await axios.delete(`${API}/caf/${tipo}/${p.id}`); toast.success('Eliminata'); carica(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Non eliminata'); }
  };
  // intestazione cliccabile: primo clic crescente, secondo decrescente
  const Intestazione = ({ campo, children }) => (
    <th className="text-left p-3">
      <button type="button" className="inline-flex items-center gap-1 hover:text-slate-900 uppercase"
        onClick={() => { setOrdine((o) => ({ campo, verso: o.campo === campo && o.verso === 'asc' ? 'desc' : 'asc' })); setPagina(1); }}>
        {children}
        {ordine.campo === campo ? (ordine.verso === 'asc' ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />)
          : <ArrowUpDown className="w-3.5 h-3.5 opacity-30" />}
      </button>
    </th>
  );
  const riquadro = (k, testo, colore) => (
    <button onClick={() => { setStato(stato === k ? '' : k); setPagina(1); }}
      className={`rounded-xl border px-4 py-2.5 text-left ${colore} ${stato === k || (!stato && k === '') ? 'ring-2 ring-slate-900' : ''}`}>
      <div className="text-2xl font-bold">{dati.conteggi[k || 'tutte'] || 0}</div><div className="text-xs">{testo}</div>
    </button>
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{nome}</h1>
          <p className="text-slate-600 mt-1">{sezione} · inserisci il cliente, poi carica {conAppuntamento ? `il ${nome}` : `la ${docNome}`} quando è fatto</p>
        </div>
        <Button onClick={() => setNuova(true)}><Plus className="w-4 h-4 mr-2" /> Nuovo {nome}</Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {riquadro('', 'Tutte', 'bg-white')}
        {riquadro('da_fare', 'Da fare', 'bg-amber-50 border-amber-200 text-amber-900')}
        {riquadro('fatto', 'Fatte', 'bg-green-50 border-green-200 text-green-900')}
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input className="pl-10" placeholder="Cerca per cliente o codice fiscale…" value={cerca} onChange={(e) => { setCerca(e.target.value); setPagina(1); }} />
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 border-b border-slate-900 bg-slate-50">
              <tr>
                <Intestazione campo="cliente">Cliente</Intestazione>
                <Intestazione campo="inserito">Inserito il</Intestazione>
                <Intestazione campo="stato">Stato</Intestazione>
                {conAppuntamento && <Intestazione campo="appuntamento">Appuntamento</Intestazione>}
                <Intestazione campo="documento">{conAppuntamento ? nome : 'Documento'}</Intestazione>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {dati.items.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-500">Nessuna pratica</td></tr>}
              {dati.items.map((p) => (
                <tr key={p.id} className="border-t border-slate-900">
                  <td className="p-3"><div className="font-medium">{p.client_name}</div><div className="text-xs text-slate-500 font-mono">{p.client_fiscal_code}</div>
                    {CON_DELEGA_GENERALE.includes(tipo) && <div className="mt-1"><DelegaGeneraleBadge clientId={p.client_id} clientName={p.client_name} /></div>}</td>
                  <td className="p-3">{dataOra(p.created_at)}<div className="text-xs text-slate-400">{p.creata_da}</div></td>
                  <td className="p-3">
                    {p.fatto
                      ? <Badge className="bg-green-100 text-green-800"><Check className="w-3.5 h-3.5 mr-1" /> Fatto il {dataOra(p.documento?.caricato_il)}</Badge>
                      : <Badge className="bg-amber-100 text-amber-800"><Clock className="w-3.5 h-3.5 mr-1" /> Da fare</Badge>}
                  </td>
                  {conAppuntamento && <td className="p-3">
                    {p.appuntamento
                      ? <button onClick={async () => { try { const r = await axios.get(`${API}/appuntamenti`, { params: { dal: p.appuntamento.inizio.slice(0, 10), al: p.appuntamento.inizio.slice(0, 10) } }); setAppAperto(r.data.appuntamenti.find((x) => x.id === p.appuntamento.id)); } catch (_) { toast.error('Appuntamento non trovato'); } }}
                          className="text-left text-blue-700 hover:underline">{quando(p.appuntamento.inizio)}{p.appuntamento.creato_da === 'cliente' && <span className="block text-[11px] text-slate-500">prenotato dall'app</span>}</button>
                      : !p.fatto && <button onClick={() => setFissa(p)} className="text-xs h-8 px-3 rounded-md border bg-white hover:bg-slate-50">Fissa appuntamento</button>}
                  </td>}
                  <td className="p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      {p.fatto && (
                        <button onClick={() => apri(p)} className="inline-flex items-center gap-1 text-blue-700 hover:underline max-w-[220px] truncate">
                          <FileText className="w-4 h-4 text-red-600 shrink-0" /> {p.documento?.nome}
                        </button>
                      )}
                      <label className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-md border text-xs cursor-pointer ${p.fatto ? 'bg-white hover:bg-slate-50' : 'bg-slate-900 text-white hover:bg-slate-800'}`}>
                        {carico === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                        {p.fatto ? 'Sostituisci' : `Carica ${docNome}`}
                        <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" className="hidden"
                          onChange={(e) => { caricaDocumento(p, e.target.files?.[0]); e.target.value = ''; }} />
                      </label>
                    </div>
                    {CON_PAD.includes(tipo) && (
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        {p.pad?.ricevuta && (
                          <button onClick={() => apriRicevutaPad(p)} className="inline-flex items-center gap-1 text-blue-700 hover:underline max-w-[220px] truncate">
                            <FileText className="w-4 h-4 text-red-600 shrink-0" /> {p.pad.ricevuta.nome}
                          </button>
                        )}
                        <label className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-md border text-xs cursor-pointer ${p.pad?.ricevuta ? 'bg-white hover:bg-slate-50' : 'bg-slate-900 text-white hover:bg-slate-800'}`}>
                          {carico === `pad-${p.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                          {p.pad?.ricevuta ? 'Sostituisci ricevuta PAD' : 'Carica ricevuta PAD'}
                          <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" className="hidden"
                            onChange={(e) => { caricaRicevutaPad(p, e.target.files?.[0]); e.target.value = ''; }} />
                        </label>
                      </div>
                    )}
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    {CON_MANDATO[tipo] && (
                      <button onClick={() => setPad(p)} title="Mandato EPAS + consenso e stampa con documento"
                        className={`inline-flex items-center gap-1 h-8 px-3 mr-2 rounded-md border text-xs ${p.pad?.ricevuta ? 'bg-green-50 border-green-200 text-green-800'
                          : p.pad?.stampato_il ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-white hover:bg-slate-50'}`}>
                        {p.pad?.ricevuta ? <Check className="w-3.5 h-3.5" /> : <Printer className="w-3.5 h-3.5" />}
                        {p.pad?.ricevuta ? `PAD fatto · ${dataOra(p.pad.ricevuta.caricato_il)}`
                          : p.pad?.stampato_il ? (CON_PAD.includes(tipo) ? 'PAD da caricare' : `${CON_MANDATO[tipo]} stampato · ${dataOra(p.pad.stampato_il)}`) : CON_MANDATO[tipo]}
                      </button>
                    )}
                    <button onClick={() => elimina(p)} className="text-red-600 p-1" title="Elimina"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
      {dati.total > 30 && (
        <div className="flex items-center justify-center gap-3">
          <Button variant="outline" size="sm" disabled={pagina === 1} onClick={() => setPagina(pagina - 1)}>Precedenti</Button>
          <span className="text-sm text-slate-500">Pagina {pagina} di {Math.ceil(dati.total / 30)}</span>
          <Button variant="outline" size="sm" disabled={pagina * 30 >= dati.total} onClick={() => setPagina(pagina + 1)}>Successive</Button>
        </div>
      )}
      {fissa && <NuovoAppuntamento tipo={tipo} cliente={{ id: fissa.client_id, nome: fissa.client_name }} onChiudi={() => setFissa(null)} onFatto={() => { setFissa(null); carica(); }} />}
      {appAperto && <DettaglioAppuntamento a={appAperto} onChiudi={() => setAppAperto(null)} onFatto={() => { setAppAperto(null); carica(); }} />}
      {pad && <FinestraPad tipo={tipo} p={pad} onChiudi={chiudiPad} onFatto={carica} />}
      {nuova && <NuovaPratica tipo={tipo} nome={nome} onChiudi={() => setNuova(false)} onCreata={() => { setNuova(false); carica(); }} />}
    </div>
  );
}
